import { AreaChart as FluentAreaChart, type ChartProps, type LineChartPoints } from '@fluentui/react-charts';
import { makeStyles, tokens } from '@fluentui/react-components';
import { useMemo } from 'react';

import { seriesColor, toneToColor } from './colors';
import { tickFormatter } from './format';
import type { ChartTone, ChartUnit, Series } from './types';

interface AreaChartProps {
  series: readonly Series[];
  /** Y-axis upper bound. Auto when omitted. For percent units, defaults to 100. */
  yMax?: number;
  /** Y-axis lower bound. Defaults to 0. */
  yMin?: number;
  /** Numeric formatting hint for the Y axis + callouts. */
  unit?: ChartUnit;
  /** Status tint applied to single-series charts. Multi-series uses the data-viz palette. */
  tone?: ChartTone;
  /** Render height in px. */
  height?: number;
  /** Hide the legend (default: hide for single series, show for multi). */
  hideLegend?: boolean;
  /** Optional accessible chart title. */
  chartTitle?: string;
}

const useStyles = makeStyles({
  wrapper: {
    width: '100%',
    color: tokens.colorNeutralForeground1,
  },
});

/**
 * Time-series area chart. Use for cumulative/volume metrics (requests, bytes,
 * exceptions). For uptime % use `tone="healthy"` — the wrapper picks the right
 * green from the Fluent palette.
 */
export function AreaChart({
  series,
  yMax,
  yMin = 0,
  unit,
  tone,
  height = 200,
  hideLegend,
  chartTitle,
}: AreaChartProps) {
  const classes = useStyles();
  const isSingle = series.length <= 1;

  const data: ChartProps = useMemo(() => {
    const lineChartData: LineChartPoints[] = series.map((s, i) => ({
      legend: s.name,
      color: s.color ?? (isSingle && tone ? toneToColor(tone) : seriesColor(i)),
      data: s.points.map((p) => ({
        x: p.t instanceof Date ? p.t : new Date(p.t),
        y: p.v,
      })),
    }));
    return {
      chartTitle,
      lineChartData,
    };
  }, [series, isSingle, tone, chartTitle]);

  const yMaxResolved = yMax ?? (unit === 'percent' ? 100 : undefined);
  // Caller wins; otherwise auto-hide the legend on single-series charts.
  const shouldHideLegend = hideLegend ?? isSingle;

  return (
    <div className={classes.wrapper}>
      <FluentAreaChart
        data={data}
        height={height}
        yMinValue={yMin}
        yMaxValue={yMaxResolved}
        yAxisTickFormat={tickFormatter(unit)}
        hideLegend={shouldHideLegend}
        reflowProps={{ mode: 'min-width' }}
      />
    </div>
  );
}
